import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Briefcase, UsersFour, CheckSquare, CreditCard, CalendarBlank, FileText,
  UserPlus, Trash, ArrowRight, User, Warning, Trophy, CurrencyDollar, Sun,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const TABS = [
  { key: "overview",     label: "Overview",      icon: Briefcase },
  { key: "athletes",     label: "My Children",   icon: UsersFour },
  { key: "attendance",   label: "Attendance",    icon: CheckSquare },
  { key: "payments",     label: "Payments",      icon: CreditCard },
  { key: "competitions", label: "Competitions",  icon: CalendarBlank },
  { key: "reports",      label: "Reports",       icon: FileText },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toDateString(); } catch { return iso; }
}
function fmtMoney(n) {
  if (!n && n !== 0) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function KpiTile({ label, value, tone = "amber", testid }) {
  const map = {
    amber: "text-amber-600",
    red: "text-red-500",
    emerald: "text-emerald-600",
    slate: "text-slate-700",
  };
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="label-tiny text-[9px] text-slate-500 mb-2">{label}</div>
      <div className={`font-heading text-3xl tabular-nums ${map[tone] || map.amber}`}>{value ?? "—"}</div>
    </div>
  );
}

// ============ ATHLETES / LINK MGMT ============
function AthletesTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [email, setEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/parent/hub/athletes").then(({ data }) => setRows(data.items || [])),
      api.get("/parent/hub/link-requests").then(({ data }) => setRequests(data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!email.trim()) return;
    setLinking(true);
    try {
      await api.post("/parent/hub/link", { email: email.trim() });
      toast.success("Request sent — your athlete must confirm before you can see their data");
      setEmail("");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setLinking(false);
    }
  };

  const cancelRequest = async (id) => {
    await api.delete(`/parent/hub/link-requests/${id}`);
    toast.success("Request removed");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Unlink this athlete? They will no longer be shared with your hub.")) return;
    await api.delete(`/parent/hub/link/${id}`);
    toast.success("Unlinked");
    load();
    onChange?.();
  };

  return (
    <div data-testid="parent-athletes">
      <div className="border border-slate-200 bg-white p-4 mb-4" data-testid="parent-link-form">
        <div className="label-tiny text-slate-600 mb-2">// REQUEST A GUARDIAN LINK</div>
        <div className="text-xs text-slate-500 mb-3">
          Enter your child&apos;s Edge Training account email. They&apos;ll get a request to confirm — you
          can only see their data once they accept.
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            className="flex-1 border border-slate-300 px-3 py-2 text-sm bg-white"
            placeholder="child@example.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            data-testid="parent-link-email"
          />
          <button
            onClick={submit}
            disabled={linking || !email.trim()}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2 flex items-center gap-2 justify-center"
            data-testid="parent-link-submit"
          >
            <UserPlus size={14} /> {linking ? "SENDING…" : "SEND REQUEST"}
          </button>
        </div>
      </div>

      {requests.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="parent-pending-requests">
          <div className="label-tiny text-slate-600">// PENDING &amp; DECLINED</div>
          {requests.map((r) => (
            <div key={r.link_id} className="border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`parent-request-${r.link_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{r.athlete_name || r.athlete_id}</div>
                <div className={`text-[10px] tracking-widest mt-0.5 ${r.status === "declined" ? "text-red-600" : "text-amber-600"}`}>
                  {r.status === "declined" ? "DECLINED" : "AWAITING CONFIRMATION"}
                </div>
              </div>
              <button
                onClick={() => cancelRequest(r.link_id)}
                className="text-slate-400 hover:text-red-600"
                data-testid={`parent-request-cancel-${r.link_id}`}
                title={r.status === "declined" ? "Remove" : "Cancel request"}
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="parent-athletes-empty">
          <UsersFour size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No confirmed children yet. Send a request above.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((a) => (
            <div key={a.user_id} className="border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={`parent-athlete-${a.user_id}`}>
              <div className="w-11 h-11 bg-amber-100 border border-amber-300 flex items-center justify-center">
                <User size={20} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base truncate">{a.name || "—"}</div>
                <div className="text-xs text-slate-500 truncate">{a.email}</div>
                <div className="text-[10px] tracking-widest text-slate-400 mt-1">{(a.primary_role || "").toUpperCase()}</div>
              </div>
              <button
                onClick={() => remove(a.user_id)}
                className="text-slate-400 hover:text-red-600"
                data-testid={`parent-unlink-${a.user_id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ WEEKLY DIGEST HERO CARD ============
function DigestHero({ setTab }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/parent/hub/digest")
      .then(({ data }) => setDigest(data))
      .catch(() => setDigest(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sendNow = async () => {
    setSending(true);
    try {
      await api.post("/parent/hub/digest/send");
      toast.success("Digest delivered to your notifications");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="parent-digest-loading">Loading weekly digest…</div>;
  }
  if (!digest || !digest.has_children) return null;

  const att = digest.attendance || {};
  const meet = digest.next_meet;
  const overdue = digest.overdue_fees || {};
  const test = digest.top_test;
  const trend = att.sessions_this_week - att.sessions_last_week;

  return (
    <div className="border border-slate-900 bg-slate-900 text-slate-50 p-5 relative overflow-hidden" data-testid="parent-digest-card">
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
      <div className="relative z-10">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-tiny text-amber-400 mb-1 flex items-center gap-1">
              <Sun size={12} weight="fill" /> WEEKLY FAMILY DIGEST · {digest.week_label?.toUpperCase()}
            </div>
            <div className="font-heading text-2xl">
              {digest.recap_line || "Quiet week — all clear."}
            </div>
          </div>
          <button
            onClick={sendNow}
            disabled={sending}
            className="border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900 text-[10px] label-tiny tracking-widest px-3 py-1.5 disabled:opacity-50 transition-colors"
            data-testid="parent-digest-send-now"
          >
            {sending ? "SENDING…" : "PUSH TO PHONE"}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <div className="border border-slate-700 p-3" data-testid="parent-digest-attendance">
            <div className="label-tiny text-slate-400 mb-1">ATTENDANCE</div>
            <div className="font-heading text-2xl text-amber-400 tabular-nums">{att.attendance_pct ?? 0}%</div>
            <div className="text-[10px] text-slate-400">
              {att.sessions_this_week} this wk{" "}
              {trend > 0 ? <span className="text-emerald-400">▲ {trend}</span>
              : trend < 0 ? <span className="text-red-400">▼ {Math.abs(trend)}</span>
              : <span className="text-slate-500">·</span>}
            </div>
          </div>

          {meet ? (
            <div className="border border-slate-700 p-3" data-testid="parent-digest-meet">
              <div className="label-tiny text-slate-400 mb-1">NEXT MEET</div>
              <div className="font-heading text-2xl text-cyan-400 tabular-nums">
                {meet.days_left != null ? `${meet.days_left}D` : "—"}
              </div>
              <div className="text-[10px] text-slate-400 truncate">{meet.name} · {meet.athlete_name}</div>
            </div>
          ) : (
            <div className="border border-slate-700 p-3">
              <div className="label-tiny text-slate-400 mb-1">NEXT MEET</div>
              <div className="font-heading text-2xl text-slate-500">—</div>
              <div className="text-[10px] text-slate-500">no upcoming</div>
            </div>
          )}

          <div className={`border p-3 ${overdue.count ? "border-red-500/60" : "border-slate-700"}`} data-testid="parent-digest-overdue">
            <div className="label-tiny text-slate-400 mb-1">OVERDUE</div>
            <div className={`font-heading text-2xl tabular-nums ${overdue.count ? "text-red-400" : "text-emerald-400"}`}>
              {overdue.count ? `₹${Number(overdue.amount || 0).toLocaleString("en-IN")}` : "₹0"}
            </div>
            <div className="text-[10px] text-slate-400 truncate">
              {overdue.count ? `${overdue.count} fee${overdue.count !== 1 ? "s" : ""} pending` : "all clear"}
            </div>
          </div>

          {test ? (
            <div className="border border-slate-700 p-3" data-testid="parent-digest-test">
              <div className="label-tiny text-slate-400 mb-1">NEW RESULT</div>
              <div className="font-heading text-2xl text-emerald-400 tabular-nums truncate">
                {test.result}{test.unit ? <span className="text-sm text-slate-400 ml-1">{test.unit}</span> : null}
              </div>
              <div className="text-[10px] text-slate-400 truncate">{test.test_name} · {test.athlete_name}</div>
            </div>
          ) : (
            <div className="border border-slate-700 p-3" data-testid="parent-digest-test">
              <div className="label-tiny text-slate-400 mb-1">NEW RESULT</div>
              <div className="font-heading text-2xl text-slate-500">—</div>
              <div className="text-[10px] text-slate-500">no tests this week</div>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-[10px] tracking-widest text-slate-400">
          <span>DELIVERED EVERY SUNDAY MORNING · {digest.children_count} CHILD{digest.children_count !== 1 ? "REN" : ""}</span>
          <button onClick={() => setTab("attendance")} className="text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="parent-digest-explore">
            EXPLORE <ArrowRight size={10} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ OVERVIEW ============
function OverviewTab({ setTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/parent/hub/overview")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!data) return <div className="text-sm text-red-500">Failed to load overview.</div>;

  const c = data.counts || {};
  const isLinked = (data.athletes || []).length > 0;

  return (
    <div className="space-y-6" data-testid="parent-overview">
      <DigestHero setTab={setTab} />

      {/* Linked strip */}
      <div className="border border-amber-300 bg-amber-50 p-4" data-testid="parent-linked-strip">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-tiny text-amber-700 mb-1">// LINKED CHILDREN ({data.athletes.length})</div>
            {isLinked ? (
              <div className="flex flex-wrap gap-2 mt-1">
                {data.athletes.map((a) => (
                  <div key={a.user_id} className="inline-flex items-center gap-2 border border-amber-400 bg-white px-3 py-1 text-sm" data-testid={`parent-strip-${a.user_id}`}>
                    <User size={14} className="text-amber-600" />
                    <span className="font-heading">{a.name || a.email}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-600 mt-1">
                No child linked yet — <button onClick={() => setTab("athletes")} className="text-amber-700 underline" data-testid="parent-link-cta">link your first child</button>.
              </div>
            )}
          </div>
          {isLinked && (
            <button onClick={() => setTab("athletes")} className="text-xs label-tiny text-amber-700 hover:text-amber-900 flex items-center gap-1" data-testid="parent-manage-cta">
              MANAGE <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>

      {isLinked && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiTile label="ATTENDANCE 30D" value={c.attendance_30d} testid="parent-kpi-attendance" />
            <KpiTile label="UPCOMING MEETS" value={c.competitions_upcoming} testid="parent-kpi-competitions" />
            <KpiTile label="RECENT TESTS" value={c.tests_recent} testid="parent-kpi-tests" />
            <KpiTile label="FEES DUE" value={c.fees_due} tone="amber" testid="parent-kpi-fees-due" />
            <KpiTile label="FEES OVERDUE" value={c.fees_overdue} tone="red" testid="parent-kpi-fees-overdue" />
            <KpiTile label="OUTSTANDING" value={fmtMoney(c.fees_outstanding_amount)} tone="red" testid="parent-kpi-outstanding" />
          </div>

          {/* Next competition + next fee side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.next_competition ? (
              <div className="border border-cyan-300 bg-cyan-50 p-4" data-testid="parent-next-competition">
                <div className="label-tiny text-cyan-700 mb-2 flex items-center gap-1"><Trophy size={12} /> NEXT COMPETITION</div>
                <div className="font-heading text-xl">{data.next_competition.name || data.next_competition.event_name || "Competition"}</div>
                <div className="text-xs text-slate-600 mt-1">
                  {data.next_competition.athlete_name} · {fmtDate(data.next_competition.date)}
                  {data.next_competition.location && <> · {data.next_competition.location}</>}
                </div>
              </div>
            ) : (
              <div className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 italic">No upcoming competitions.</div>
            )}
            {data.next_fee_due ? (
              <div className="border border-red-300 bg-red-50 p-4" data-testid="parent-next-fee">
                <div className="label-tiny text-red-700 mb-2 flex items-center gap-1"><CurrencyDollar size={12} /> NEXT FEE DUE</div>
                <div className="font-heading text-xl">{fmtMoney(data.next_fee_due.amount)}</div>
                <div className="text-xs text-slate-600 mt-1">
                  {data.next_fee_due.athlete_name} · {data.next_fee_due.description || data.next_fee_due.title || "Fee"} · due {fmtDate(data.next_fee_due.due_date)}
                </div>
              </div>
            ) : (
              <div className="border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800">All fees settled.</div>
            )}
          </div>

          {/* Recent tests + attendance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-slate-200 bg-white p-4" data-testid="parent-recent-tests">
              <div className="label-tiny mb-3 text-slate-700">// RECENT TESTS</div>
              {data.recent_tests?.length ? (
                <div className="space-y-2">
                  {data.recent_tests.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                      <div className="min-w-0">
                        <div className="font-heading truncate">{t.test_name || t.name || "Test"}</div>
                        <div className="text-slate-500 truncate">{t.athlete_name}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="tabular-nums text-slate-700">{t.result != null ? t.result : "—"}{t.unit ? ` ${t.unit}` : ""}</div>
                        <div className="text-[10px] text-slate-400">{fmtDate(t.test_date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-slate-500 italic">No recent tests.</div>}
            </div>
            <div className="border border-slate-200 bg-white p-4" data-testid="parent-recent-attendance">
              <div className="label-tiny mb-3 text-slate-700">// RECENT SESSIONS</div>
              {data.recent_attendance?.length ? (
                <div className="space-y-2">
                  {data.recent_attendance.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                      <div className="min-w-0">
                        <div className="font-heading truncate">{a.name || "Session"}</div>
                        <div className="text-slate-500 truncate">{a.athlete_name}</div>
                      </div>
                      <div className="text-[10px] text-slate-400 shrink-0">{fmtDate(a.date)}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-slate-500 italic">No recent sessions.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ ATTENDANCE ============
function AttendanceTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setLoading(true);
    api.get(`/parent/hub/attendance?days=${days}`)
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  }, [days]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = r.athlete_name || "—";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return Array.from(m.entries());
  }, [rows]);

  return (
    <div data-testid="parent-attendance">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <div className="label-tiny text-slate-500">// ATTENDANCE</div>
          <div className="text-sm text-slate-600">{rows.length} sessions across all children</div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 px-2 py-1 text-xs bg-white" data-testid="parent-attendance-range">
          <option value={30}>LAST 30 DAYS</option>
          <option value={90}>LAST 90 DAYS</option>
          <option value={180}>LAST 6 MONTHS</option>
          <option value={365}>LAST YEAR</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="parent-attendance-empty">
          <CheckSquare size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No sessions logged in this window.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([name, items]) => (
            <div key={name}>
              <div className="label-tiny text-amber-700 mb-2">// {name.toUpperCase()} · {items.length} SESSIONS</div>
              <div className="border border-slate-200 bg-white">
                {items.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-100 last:border-0" data-testid={`parent-att-row-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-heading truncate">{r.name || "Session"}</div>
                      {r.notes && <div className="text-xs text-slate-500 truncate">{r.notes}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs tabular-nums text-slate-700">{fmtDate(r.date)}</div>
                      {r.duration_minutes && <div className="text-[10px] text-slate-400">{r.duration_minutes} min</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ PAYMENTS ============
function PaymentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/parent/hub/payments")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const all = data?.items || [];
    if (!statusFilter) return all;
    return all.filter((r) => (r.status || "").toLowerCase() === statusFilter);
  }, [data, statusFilter]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  const s = data?.summary || {};

  return (
    <div data-testid="parent-payments">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiTile label="OUTSTANDING" value={fmtMoney(s.outstanding_amount)} tone="red" testid="pay-kpi-outstanding" />
        <KpiTile label="OVERDUE" value={s.overdue} tone="red" testid="pay-kpi-overdue" />
        <KpiTile label="DUE" value={s.due} tone="amber" testid="pay-kpi-due" />
        <KpiTile label="PAID" value={s.paid} tone="emerald" testid="pay-kpi-paid" />
      </div>

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-sm text-slate-600">{rows.length} fee entries</div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 px-2 py-1 text-xs bg-white" data-testid="parent-payments-filter">
          <option value="">ALL STATUSES</option>
          <option value="overdue">OVERDUE</option>
          <option value="due">DUE</option>
          <option value="pending">PENDING</option>
          <option value="paid">PAID</option>
        </select>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="parent-payments-empty">
          <CreditCard size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No fee entries.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
            <div className="col-span-3">ATHLETE</div>
            <div className="col-span-4">DESCRIPTION</div>
            <div className="col-span-2">DUE DATE</div>
            <div className="col-span-2 text-right">AMOUNT</div>
            <div className="col-span-1 text-right">STATUS</div>
          </div>
          {rows.map((r, i) => {
            const st = (r.status || "").toLowerCase();
            const stColor = st === "paid" ? "text-emerald-600" : st === "overdue" ? "text-red-600" : "text-amber-600";
            return (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-b border-slate-100 last:border-0" data-testid={`parent-pay-row-${i}`}>
                <div className="col-span-3 truncate">{r.athlete_name}</div>
                <div className="col-span-4 truncate">{r.description || r.title || "Fee"}</div>
                <div className="col-span-2 text-slate-500">{r.due_date || "—"}</div>
                <div className="col-span-2 text-right font-heading tabular-nums">{fmtMoney(r.amount)}</div>
                <div className={`col-span-1 text-right label-tiny ${stColor}`}>{st.toUpperCase() || "—"}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ COMPETITIONS ============
function CompetitionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/parent/hub/competitions")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  const upcoming = data?.upcoming || [];
  const past = data?.past || [];

  const renderRow = (c, i, tone) => (
    <div key={i} className={`border ${tone} bg-white p-3 flex items-start justify-between gap-3`} data-testid={`parent-comp-row-${i}`}>
      <div className="min-w-0">
        <div className="font-heading text-sm truncate">{c.name || c.event_name || "Competition"}</div>
        <div className="text-xs text-slate-500 truncate">{c.athlete_name} · {c.location || c.venue || ""}</div>
        {c.notes && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.notes}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs tabular-nums font-heading">{fmtDate(c.date)}</div>
        {c.result && <div className="text-[10px] text-emerald-600 mt-1">{c.result}</div>}
      </div>
    </div>
  );

  return (
    <div data-testid="parent-competitions">
      <div className="mb-4">
        <div className="label-tiny text-slate-500">// COMPETITIONS</div>
        <div className="text-sm text-slate-600">{upcoming.length} upcoming · {past.length} past</div>
      </div>

      <div>
        <div className="label-tiny text-cyan-700 mb-2">// UPCOMING ({upcoming.length})</div>
        {upcoming.length === 0 ? (
          <div className="text-sm text-slate-500 italic mb-6">No upcoming competitions.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
            {upcoming.map((c, i) => renderRow(c, i, "border-cyan-300"))}
          </div>
        )}
      </div>

      <div>
        <div className="label-tiny text-slate-500 mb-2">// PAST ({past.length})</div>
        {past.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No past competitions.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {past.map((c, i) => renderRow(c, i, "border-slate-200"))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ REPORTS ============
function ReportsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/parent/hub/reports")
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = r.athlete_name || "—";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return Array.from(m.entries());
  }, [rows]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="parent-reports">
      <div className="mb-4">
        <div className="label-tiny text-slate-500">// REPORTS & TESTS</div>
        <div className="text-sm text-slate-600">{rows.length} test results across all children</div>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="parent-reports-empty">
          <FileText size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No test results yet.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([name, items]) => (
            <div key={name}>
              <div className="label-tiny text-amber-700 mb-2">// {name.toUpperCase()} · {items.length}</div>
              <div className="border border-slate-200 bg-white">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                  <div className="col-span-5">TEST</div>
                  <div className="col-span-3">DATE</div>
                  <div className="col-span-2 text-right">RESULT</div>
                  <div className="col-span-2 text-right">UNIT</div>
                </div>
                {items.map((t, i) => {
                  const val = t.result ?? t.value ?? t.result_value ?? t.metric_value ?? t.score;
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-b border-slate-100 last:border-0" data-testid={`parent-report-row-${i}`}>
                      <div className="col-span-5 truncate">{t.test_name || t.name || "Test"}</div>
                      <div className="col-span-3 text-slate-500">{fmtDate(t.test_date)}</div>
                      <div className="col-span-2 text-right tabular-nums font-heading">{val != null && val !== "" ? val : "—"}</div>
                      <div className="col-span-2 text-right text-slate-500">{t.unit || ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ MAIN ============
export default function ParentCommandDeck() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  const tab = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return TABS.find((x) => x.key === t)?.key || "overview";
  }, [location.search]);

  const setTab = (key) => navigate(`/parent${key === "overview" ? "" : `?tab=${key}`}`);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="parent-command-deck">
      <div className="mb-6">
        <div className="label-tiny text-amber-600 mb-2">// PARENT / GUARDIAN · COMMAND DECK</div>
        <h1 className="font-heading text-4xl sm:text-5xl">FAMILY HUB</h1>
        <p className="text-slate-600 text-sm mt-2">
          {user?.name?.split(" ")[0] || "Parent"} · Read-only mirror of your children&apos;s attendance, payments, competitions &amp; reports.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto no-scrollbar" data-testid="parent-tabs">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`parent-tab-${key}`}
              className={`flex items-center gap-2 px-3 py-2.5 text-xs label-tiny tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                active
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon size={14} weight={active ? "fill" : "duotone"} />
              {label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {tab === "overview"     && <OverviewTab setTab={setTab} key={refreshKey} />}
      {tab === "athletes"     && <AthletesTab onChange={() => setRefreshKey((k) => k + 1)} />}
      {tab === "attendance"   && <AttendanceTab />}
      {tab === "payments"     && <PaymentsTab />}
      {tab === "competitions" && <CompetitionsTab />}
      {tab === "reports"      && <ReportsTab />}
    </div>
  );
}
